import { useMemo, useState } from "react";
import type { BreedingInventorySnapshot } from "../breeding/liveBreedingInventory";
import {
  buildBreederCreateOperation,
  buildBreederUpdateOperation,
  buildPairEndOperation,
  buildPairFormOperation,
  buildPairMoveOperation,
  buildStockTransferOperation,
} from "../operations/breedingInventoryOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";
import { speakPersian } from "./audioGuidance";

export type BreedingManagerAction =
  | "breeder_create"
  | "breeder_update"
  | "pair_form"
  | "pair_move"
  | "pair_separate"
  | "pair_exit"
  | "stock_transfer";

interface BreedingManagementPanelProps {
  farmId: string;
  userId: string;
  snapshot: BreedingInventorySnapshot;
  initialAction?: BreedingManagerAction;
  showActionPicker?: boolean;
  preferredPairId?: string;
  preferredTargetTankId?: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
  onCompleted: () => void;
}

const ACTION_META: Record<BreedingManagerAction, { label: string; icon: string }> = {
  breeder_create: { label: "افزودن مولد", icon: "🐟" },
  breeder_update: { label: "ویرایش مولد", icon: "✏️" },
  pair_form: { label: "تشکیل جفت", icon: "💞" },
  pair_move: { label: "جابجایی جفت", icon: "↔️" },
  pair_separate: { label: "جداسازی جفت", icon: "✂️" },
  pair_exit: { label: "خروج جفت از مجموعه", icon: "🚪" },
  stock_transfer: { label: "انتقال / تقسیم گله", icon: "🐠" },
};

function syncMessage(status: string): string {
  if (status === "synced") return "عملیات ثبت و همگام شد.";
  if (status === "pending") return "عملیات آفلاین ثبت شد و در صف ارسال قرار گرفت.";
  if (status === "retryable_error") return "ثبت محلی حفظ شد؛ ارسال دوباره انجام می‌شود.";
  if (status === "terminal_conflict") return "ثبت محلی حفظ شد اما نیاز به بررسی تعارض دارد.";
  return "عملیات ثبت شد.";
}

export function BreedingManagementPanel({
  farmId,
  userId,
  snapshot,
  initialAction,
  showActionPicker = true,
  preferredPairId,
  preferredTargetTankId,
  onSyncSummary,
  onCompleted,
}: BreedingManagementPanelProps) {
  const [action, setAction] = useState<BreedingManagerAction | undefined>(initialAction);
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const activeTanks = useMemo(() => snapshot.tanks.filter((tank) => tank.active), [snapshot.tanks]);
  const activePairs = useMemo(
    () => snapshot.pairs.filter((pair) => pair.status === "active"),
    [snapshot.pairs],
  );
  const activeBreeders = useMemo(
    () => snapshot.breeders.filter((breeder) => breeder.status === "active"),
    [snapshot.breeders],
  );
  const activeMaleBreeders = activeBreeders.filter((breeder) => breeder.sex === "male");
  const activeFemaleBreeders = activeBreeders.filter((breeder) => breeder.sex === "female");
  const activeStocks = useMemo(
    () => snapshot.stockGroups.filter((stock) => stock.status === "active"),
    [snapshot.stockGroups],
  );

  const [breederCode, setBreederCode] = useState("");
  const [breederSex, setBreederSex] = useState<"male" | "female" | "unknown">("unknown");
  const [breederTankId, setBreederTankId] = useState("");
  const [breederId, setBreederId] = useState(snapshot.breeders[0]?.id ?? "");
  const selectedBreeder = snapshot.breeders.find((item) => item.id === breederId);
  const [breederStatus, setBreederStatus] = useState<"active" | "retired" | "deceased" | "exited">(
    "active",
  );

  const [pairCode, setPairCode] = useState("");
  const [maleBreederId, setMaleBreederId] = useState(activeMaleBreeders[0]?.id ?? "");
  const [femaleBreederId, setFemaleBreederId] = useState(activeFemaleBreeders[0]?.id ?? "");
  const [pairId, setPairId] = useState(preferredPairId ?? activePairs[0]?.id ?? "");
  const [targetTankId, setTargetTankId] = useState(
    preferredTargetTankId ?? activeTanks[0]?.id ?? "",
  );
  const [maleTargetTankId, setMaleTargetTankId] = useState("");
  const [femaleTargetTankId, setFemaleTargetTankId] = useState("");
  const [reason, setReason] = useState("");

  const [stockGroupId, setStockGroupId] = useState(activeStocks[0]?.id ?? "");
  const [moveQuantity, setMoveQuantity] = useState("");
  const selectedStock = activeStocks.find((stock) => stock.id === stockGroupId);

  function context() {
    return {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      performedAt: new Date().toISOString(),
    };
  }

  function requestConfirmation() {
    setError(undefined);
    setMessage(undefined);

    if (!action) {
      setError("ابتدا عملیات مورد نظر را انتخاب کنید.");
      return;
    }

    try {
      let operation: OfflineOperation;
      const parts = [ACTION_META[action].label];

      if (action === "breeder_create") {
        operation = buildBreederCreateOperation(context(), {
          breederCode,
          sex: breederSex,
          ...(breederTankId ? { targetTankId: breederTankId } : {}),
        });
        parts.push(`کد مولد: ${breederCode}`);
      } else if (action === "breeder_update") {
        if (!selectedBreeder) throw new Error("مولد انتخاب نشده است.");
        operation = buildBreederUpdateOperation(context(), {
          breederId: selectedBreeder.id,
          breederVersion: selectedBreeder.domainVersion,
          breederCode: breederCode.trim() || selectedBreeder.breederCode,
          sex: breederSex === "unknown" ? selectedBreeder.sex : breederSex,
          status: breederStatus,
          ...(breederTankId ? { targetTankId: breederTankId } : {}),
        });
        parts.push(`مولد: ${selectedBreeder.breederCode}`, `وضعیت: ${breederStatus}`);
      } else if (action === "pair_form") {
        operation = buildPairFormOperation(context(), {
          pairCode,
          maleBreederId,
          femaleBreederId,
          targetTankId,
          ...(reason.trim() ? { notes: reason.trim() } : {}),
        });
        parts.push(`کد جفت: ${pairCode}`);
      } else if (action === "pair_move") {
        const pair = activePairs.find((item) => item.id === pairId);
        if (!pair) throw new Error("جفت فعال انتخاب نشده است.");
        operation = buildPairMoveOperation(context(), {
          pairId: pair.id,
          pairVersion: pair.domainVersion,
          targetTankId,
        });
        parts.push(`جفت: ${pair.pairCode}`);
      } else if (action === "pair_separate" || action === "pair_exit") {
        const pair = activePairs.find((item) => item.id === pairId);
        if (!pair) throw new Error("جفت فعال انتخاب نشده است.");
        operation = buildPairEndOperation(context(), {
          pairId: pair.id,
          pairVersion: pair.domainVersion,
          mode: action === "pair_exit" ? "exit" : "separate",
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(action === "pair_separate" && maleTargetTankId ? { maleTargetTankId } : {}),
          ...(action === "pair_separate" && femaleTargetTankId ? { femaleTargetTankId } : {}),
        });
        parts.push(
          `جفت: ${pair.pairCode}`,
          action === "pair_exit"
            ? "هر دو مولد از مجموعه خارج می‌شوند"
            : "جفت پایان می‌یابد و مولدها مستقل می‌شوند",
        );
      } else {
        const stock = activeStocks.find((item) => item.id === stockGroupId);
        if (!stock) throw new Error("گله فعال انتخاب نشده است.");
        const quantity = moveQuantity.trim() ? Number(moveQuantity) : undefined;
        if (stock.quantityPrecision === "unknown" && quantity !== undefined) {
          throw new Error(
            "برای گله با تعداد نامشخص، تقسیم تعدادی مجاز نیست؛ انتقال کامل انجام دهید.",
          );
        }
        operation = buildStockTransferOperation(context(), {
          stockGroupId: stock.id,
          stockVersion: stock.domainVersion,
          targetTankId,
          ...(quantity === undefined ? {} : { moveQuantity: quantity }),
        });
        parts.push(
          stock.groupCode ? `گله: ${stock.groupCode}` : "گله انتخاب‌شده",
          quantity === undefined ? "انتقال کامل" : `تقسیم و انتقال ${quantity} عدد`,
        );
      }

      setPending(operation);
      setConfirmationParts(parts);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات عملیات معتبر نیست.");
    }
  }

  async function confirm() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        onCompleted();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت عملیات انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="manager-card">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">تکثیر</p>
          <h3>{action ? ACTION_META[action].label : "عملیات مدیریتی"}</h3>
        </div>
      </div>

      {showActionPicker ? (
        <>
          <button
            type="button"
            className="secondary-button large-button"
            onClick={() =>
              speakPersian(
                "عملیات مورد نظر را انتخاب کنید. تشکیل جفت، جابجایی، جداسازی، خروج جفت، افزودن مولد یا انتقال گله.",
              )
            }
          >
            🔊 راهنمای صوتی
          </button>

          <div className="quick-grid">
            {(Object.keys(ACTION_META) as BreedingManagerAction[]).map((item) => (
              <button
                type="button"
                className={action === item ? "quick-button active" : "quick-button"}
                key={item}
                aria-label={ACTION_META[item].label}
                onClick={() => {
                  setAction(item);
                  setPending(undefined);
                  setError(undefined);
                  setMessage(undefined);
                  speakPersian(`${ACTION_META[item].label} انتخاب شد`);
                }}
              >
                <span aria-hidden="true">{ACTION_META[item].icon}</span>
                <strong>{ACTION_META[item].label}</strong>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {action === "breeder_create" ? (
        <div className="operation-field-grid">
          <label>
            کد مولد
            <input value={breederCode} onChange={(event) => setBreederCode(event.target.value)} />
          </label>
          <label>
            جنس
            <select
              value={breederSex}
              onChange={(event) =>
                setBreederSex(event.target.value as "male" | "female" | "unknown")
              }
            >
              <option value="male">نر</option>
              <option value="female">ماده</option>
              <option value="unknown">نامشخص</option>
            </select>
          </label>
          <label>
            تانک اختیاری
            <select
              value={breederTankId}
              onChange={(event) => setBreederTankId(event.target.value)}
            >
              <option value="">بدون تخصیص تانک</option>
              {activeTanks.map((tank) => (
                <option value={tank.id} key={tank.id}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {action === "breeder_update" ? (
        <div className="operation-field-grid">
          <label>
            مولد
            <select value={breederId} onChange={(event) => setBreederId(event.target.value)}>
              {snapshot.breeders.map((breeder) => (
                <option value={breeder.id} key={breeder.id}>
                  {breeder.breederCode}
                </option>
              ))}
            </select>
          </label>
          <label>
            کد جدید (اختیاری)
            <input value={breederCode} onChange={(event) => setBreederCode(event.target.value)} />
          </label>
          <label>
            جنس
            <select
              value={breederSex}
              onChange={(event) =>
                setBreederSex(event.target.value as "male" | "female" | "unknown")
              }
            >
              <option value="unknown">بدون تغییر</option>
              <option value="male">نر</option>
              <option value="female">ماده</option>
            </select>
          </label>
          <label>
            وضعیت
            <select
              value={breederStatus}
              onChange={(event) =>
                setBreederStatus(event.target.value as "active" | "retired" | "deceased" | "exited")
              }
            >
              <option value="active">فعال</option>
              <option value="retired">بازنشسته</option>
              <option value="deceased">تلف‌شده</option>
              <option value="exited">خارج‌شده</option>
            </select>
          </label>
          <label>
            انتقال به تانک (اختیاری)
            <select
              value={breederTankId}
              onChange={(event) => setBreederTankId(event.target.value)}
            >
              <option value="">بدون تغییر تانک</option>
              {activeTanks.map((tank) => (
                <option value={tank.id} key={tank.id}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {action === "pair_form" ? (
        <div className="operation-field-grid">
          <label>
            کد جفت
            <input value={pairCode} onChange={(event) => setPairCode(event.target.value)} />
          </label>
          <label>
            مولد نر
            <select
              value={maleBreederId}
              onChange={(event) => setMaleBreederId(event.target.value)}
            >
              {activeMaleBreeders.map((breeder) => (
                <option value={breeder.id} key={breeder.id}>
                  {breeder.breederCode}
                </option>
              ))}
            </select>
          </label>
          <label>
            مولد ماده
            <select
              value={femaleBreederId}
              onChange={(event) => setFemaleBreederId(event.target.value)}
            >
              {activeFemaleBreeders.map((breeder) => (
                <option value={breeder.id} key={breeder.id}>
                  {breeder.breederCode}
                </option>
              ))}
            </select>
          </label>
          <label>
            تانک جفت
            <select value={targetTankId} onChange={(event) => setTargetTankId(event.target.value)}>
              {activeTanks.map((tank) => (
                <option value={tank.id} key={tank.id}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {action === "pair_move" || action === "pair_separate" || action === "pair_exit" ? (
        <div className="operation-field-grid">
          <label>
            جفت فعال
            <select value={pairId} onChange={(event) => setPairId(event.target.value)}>
              {activePairs.map((pair) => (
                <option value={pair.id} key={pair.id}>
                  {pair.pairCode}
                </option>
              ))}
            </select>
          </label>
          {action === "pair_move" ? (
            <label>
              تانک مقصد
              <select
                value={targetTankId}
                onChange={(event) => setTargetTankId(event.target.value)}
              >
                {activeTanks.map((tank) => (
                  <option value={tank.id} key={tank.id}>
                    {tank.tankCode}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {action === "pair_separate" ? (
            <>
              <label>
                تانک نر (اختیاری)
                <select
                  value={maleTargetTankId}
                  onChange={(event) => setMaleTargetTankId(event.target.value)}
                >
                  <option value="">بدون تخصیص</option>
                  {activeTanks.map((tank) => (
                    <option value={tank.id} key={tank.id}>
                      {tank.tankCode}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                تانک ماده (اختیاری)
                <select
                  value={femaleTargetTankId}
                  onChange={(event) => setFemaleTargetTankId(event.target.value)}
                >
                  <option value="">بدون تخصیص</option>
                  {activeTanks.map((tank) => (
                    <option value={tank.id} key={tank.id}>
                      {tank.tankCode}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          {action !== "pair_move" ? (
            <label>
              علت / توضیح
              <input value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
          ) : null}
        </div>
      ) : null}

      {action === "stock_transfer" ? (
        <div className="operation-field-grid">
          <label>
            گله
            <select value={stockGroupId} onChange={(event) => setStockGroupId(event.target.value)}>
              {activeStocks.map((stock) => (
                <option value={stock.id} key={stock.id}>
                  {stock.groupCode ?? stock.id.slice(0, 8)}
                  {stock.quantityPrecision === "unknown" ? " · تعداد نامشخص" : ""}
                </option>
              ))}
            </select>
          </label>
          <label>
            تانک مقصد
            <select value={targetTankId} onChange={(event) => setTargetTankId(event.target.value)}>
              {activeTanks.map((tank) => (
                <option value={tank.id} key={tank.id}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>
          <label>
            تعداد برای Split (خالی = انتقال کامل)
            <input
              type="number"
              min="1"
              step="1"
              disabled={selectedStock?.quantityPrecision === "unknown"}
              value={selectedStock?.quantityPrecision === "unknown" ? "" : moveQuantity}
              onChange={(event) => setMoveQuantity(event.target.value)}
            />
          </label>
        </div>
      ) : null}

      {action ? (
        <button
          type="button"
          className="primary-button large-button"
          disabled={busy}
          onClick={requestConfirmation}
        >
          {busy ? "در حال ثبت…" : "بررسی و تأیید"}
        </button>
      ) : (
        <p className="section-note">یکی از عملیات بالا را انتخاب کنید.</p>
      )}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید عملیات مدیریت تکثیر"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </article>
  );
}
